import { create } from "zustand";
import type { AuthUser, CollectionName } from "@lockcal/shared";
import {
  api,
  ApiError,
  getStoredUser,
  getToken,
  setStoredUser,
  setToken,
} from "@/api/client";
import {
  clearAll,
  getRecord,
  putRecord,
  type LocalRecord,
} from "@/data/db";
import { loadAll, syncNow } from "@/data/sync";

type Status = "loading" | "signed-out" | "signed-in";

export interface AppState {
  status: Status;
  user: AuthUser | null;
  data: Record<CollectionName, LocalRecord[]>;
  syncing: boolean;
  lastSyncAt: number | null;
  syncError: string | null;
  googleClientId: string | null;
  /** Message for the sign-in screen, for example a failed Google round trip. */
  authNotice: string | null;

  init: () => Promise<void>;
  login: (email: string, password: string) => Promise<void>;
  register: (
    email: string,
    password: string,
    displayName?: string,
  ) => Promise<void>;
  loginWithGoogle: (idToken: string) => Promise<void>;
  /** Finishes the Google round trip with the code the server handed back. */
  completeGoogleSignIn: (code: string) => Promise<void>;
  logout: () => Promise<void>;
  sync: () => Promise<void>;
  upsert: (collection: CollectionName, record: LocalRecord) => Promise<void>;
  remove: (collection: CollectionName, id: string) => Promise<void>;
  reload: () => Promise<void>;
}

/** Human readable reason for a failed Google round trip. */
function describeGoogleError(reason: string): string {
  if (reason.includes("access_denied")) return "Bạn đã huỷ đăng nhập Google.";
  if (reason.includes("exchange"))
    return "Google từ chối mã đăng nhập. Hãy thử lại.";
  if (reason.includes("no_code")) return "Google không trả về mã đăng nhập.";
  return `Đăng nhập Google thất bại: ${reason}`;
}

const EMPTY: Record<CollectionName, LocalRecord[]> = {
  calendars: [],
  events: [],
  reminderLists: [],
  reminders: [],
  habits: [],
  habitCheckIns: [],
  stickyNotes: [],
  settings: [],
};

/**
 * Automatic sync is debounced: the note editor writes every few hundred
 * milliseconds while typing, and hitting the server for each keystroke would
 * be pointless.
 */
let syncTimer: number | undefined;
let syncTimerDelay = 1500;

export const useAppStore = create<AppState>()((set, get) => ({
  status: "loading",
  user: null,
  data: EMPTY,
  syncing: false,
  lastSyncAt: null,
  syncError: null,
  // The server decides whether Google sign-in is available, so the client ID
  // does not have to be baked into the web bundle. `init` fills it in.
  googleClientId: null,
  authNotice: null,

  async init() {
    // The Google round trip comes back here with either a code or a reason.
    const params = new URLSearchParams(window.location.search);
    const googleCode = params.get("googleCode");
    const googleError = params.get("googleError");
    if (googleCode || googleError) {
      params.forEach((_value, key) => {
        if (key === "googleCode" || key === "googleError") params.delete(key);
      });
      const clean = params.toString();
      window.history.replaceState(
        {},
        "",
        `${window.location.pathname}${clean ? `?${clean}` : ""}`,
      );
    }

    if (googleError) {
      set({
        status: "signed-out",
        authNotice: describeGoogleError(googleError),
      });
      return;
    }
    if (googleCode) {
      try {
        await get().completeGoogleSignIn(googleCode);
        return;
      } catch (err) {
        set({ status: "signed-out", authNotice: describeGoogleError(String(err)) });
        return;
      }
    }

    // Sign-in options never block the app: if the request fails the password
    // form still works.
    void api
      .authConfig()
      .then((config) => set({ googleClientId: config.googleClientId }))
      .catch(() => undefined);

    const token = getToken();
    if (!token) {
      set({ status: "signed-out" });
      return;
    }
    try {
      const { user } = await api.me();
      setStoredUser(user);
      set({ user, status: "signed-in" });
      await get().reload();
      await get().sync();
    } catch (err) {
      if (err instanceof ApiError && err.status === 401) {
        setToken(null);
        setStoredUser(null);
        set({ status: "signed-out" });
        return;
      }
      set({ status: "signed-out" });
    }
  },

  async login(email, password) {
    const response = await api.login(email, password);
    setToken(response.token);
    setStoredUser(response.user);
    set({ user: response.user, status: "signed-in" });
    await get().reload();
    await get().sync();
  },

  async register(email, password, displayName) {
    const response = await api.register(email, password, displayName);
    setToken(response.token);
    setStoredUser(response.user);
    set({ user: response.user, status: "signed-in" });
    await get().reload();
    await get().sync();
  },

  async loginWithGoogle(idToken) {
    const response = await api.google(idToken);
    setToken(response.token);
    setStoredUser(response.user);
    set({ user: response.user, status: "signed-in" });
    await get().reload();
    await get().sync();
  },

  async completeGoogleSignIn(code) {
    const response = await api.googleExchange(code);
    setToken(response.token);
    setStoredUser(response.user);
    set({ user: response.user, status: "signed-in", authNotice: null });
    await get().reload();
    await get().sync();
  },

  async logout() {
    setToken(null);
    setStoredUser(null);
    await clearAll();
    set({ user: null, status: "signed-out", data: EMPTY, lastSyncAt: null });
  },

  async reload() {
    let data = await loadAll();
    // The settings row is a singleton. Create it on demand so that the
    // settings screen always has something to write to.
    if (!data.settings.some((s) => s.id === "singleton")) {
      await putRecord("settings", {
        id: "singleton",
        locale: "vi",
        themeMode: "system",
        defaultWidgetLayout: "home",
        googleCalendarEnabled: false,
        showHabitWidgets: true,
        use24HourClock: true,
        firstDayOfWeek: 1,
        createdAt: Date.now(),
        updatedAt: Date.now(),
        deletedAt: null,
        _dirty: 1,
        _deleted: 0,
      });
      data = await loadAll();
    }
    set({ data });
  },

  async sync() {
    if (get().syncing) return;
    set({ syncing: true, syncError: null });
    const result = await syncNow();
    await get().reload();
    set({
      syncing: false,
      lastSyncAt: Date.now(),
      syncError: result.error ?? null,
    });
  },

  async upsert(collection, record) {
    const existing = await getRecord(collection, record.id);
    const next: LocalRecord = {
      ...existing,
      ...record,
      updatedAt: record.updatedAt ?? Date.now(),
      _dirty: 1,
      _deleted: record.deletedAt ? 1 : 0,
    };
    await putRecord(collection, next);
    await get().reload();
    scheduleSync();
  },

  async remove(collection, id) {
    const existing = await getRecord(collection, id);
    if (!existing) return;
    const now = Date.now();
    await putRecord(collection, {
      ...existing,
      deletedAt: now,
      updatedAt: now,
      _dirty: 1,
      _deleted: 1,
    });
    await get().reload();
    scheduleSync();
  },
}));

/** Queue a debounced background sync. */
function scheduleSync(): void {
  if (syncTimer !== undefined) window.clearTimeout(syncTimer);
  syncTimer = window.setTimeout(() => {
    syncTimer = undefined;
    void useAppStore.getState().sync();
  }, syncTimerDelay);
}

/**
 * The settings row is a singleton, created on demand by `reload()`.
 */

/** Only records that are not tombstoned. */
export function live<T>(records: LocalRecord[] | undefined): T[] {
  return (records ?? []).filter((r) => !r.deletedAt) as unknown as T[];
}
